import {
  addDoc,
  collection,
  connectFirestoreEmulator,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  startAfter,
  updateDoc,
  where,
  setDoc,
} from 'firebase/firestore';
import _ from 'lodash';
import APP from './app';
import { EMULATOR_CONFIG, USE_EMULATOR } from './config';

// ------------------------------------------------------------

const DB = getFirestore(APP);

const FIRESTORE_EMULATOR_HOST = EMULATOR_CONFIG.FIRESTORE?.host || '127.0.0.1';
const FIRESTORE_EMULATOR_PORT = Number(EMULATOR_CONFIG.FIRESTORE?.port) || 8080;

if (USE_EMULATOR) {
  connectFirestoreEmulator(DB, FIRESTORE_EMULATOR_HOST, FIRESTORE_EMULATOR_PORT);
}

/**
 * Add document
 * @param {string} collectionName - Collection name
 * @param {Object} data - Data
 * @returns {Promise<void>}
 */
export const _addDoc = async (collectionName, data) => {
  const collectionRef = collection(DB, collectionName);

  let docRef;

  if (data.id) {
    docRef = doc(collectionRef, data.id);
  } else {
    docRef = doc(collectionRef);
  }

  await setDoc(docRef, { ...data, createdAt: new Date().getTime() });
};

/**
 * Update document
 * @param {string} collectionName - Collection name
 * @param {string} id - Document id
 * @param {Object} data - Data
 * @returns {Promise<void>}
 */
export const _updateDoc = async (collectionName, id, data) => {
  const collectionRef = collection(DB, collectionName);
  await updateDoc(doc(collectionRef, id), { ...data, updatedAt: new Date().getTime() });
};

/**
 * Delete document
 * @param {string} collectionName - Collection name
 * @param {string} id - Document id
 * @returns {Promise<void>}
 */
export const _deleteDoc = async (collectionName, id) => {
  const collectionRef = collection(DB, collectionName);
  await deleteDoc(doc(collectionRef, id));
};

/**
 * Delete many documents
 * @param {string} collectionName - Collection name
 * @param {Array} ids - Document ids
 * @returns {Promise<void>}
 */
export const deleteManyDocs = async (collectionName, ids) => {
  const collectionRef = collection(DB, collectionName);
  await Promise.all(ids.map(id => deleteDoc(doc(collectionRef, id))));
};

/**
 * Get document by id
 * @param {string} collectionName - Collection name
 * @param {string} id - Document id
 * @returns {Promise<Object>}
 */
export const getDocById = async (collectionName, id) => {
  const collectionRef = collection(DB, collectionName);
  const docRef = doc(collectionRef, id);
  const docSnap = await getDoc(docRef);
  const data = docSnap.data();
  return { id: docSnap.id, ...data };
};

/**
 * Get documents by field
 * @param {string} collectionName - Collection name
 * @param {string} field - Field name
 * @param {string} value - Field value
 * @param {string|null} _orderBy - Order by field (null = no ordering; all matching docs returned)
 * @param {string} order - Order
 * @returns {Promise<Array>}
 */
export const getDocsByField = async (collectionName, field, value, _orderBy = 'createdAt', order = 'desc') => {
  const collectionRef = collection(DB, collectionName);
  const queryRef =
    _orderBy != null && _orderBy !== ''
      ? query(collectionRef, where(field, '==', value), orderBy(_orderBy, order))
      : query(collectionRef, where(field, '==', value));
  const docs = await getDocs(queryRef);
  return docs.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

/**
 * Get all documents
 * @param {string} collectionName - Collection name
 * @returns {Promise<Array>}
 */
export const getAllDocs = async collectionName => {
  const collectionRef = collection(DB, collectionName);
  const queryRef = query(collectionRef, orderBy('createdAt', 'desc'));
  const docs = await getDocs(queryRef);
  return docs.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

/**
 * Get all documents with pagination
 * @param {string} collectionName - Collection name
 * @param {string} startAfterValue - Start after value
 * @param {Array} filters - Filters
 * @param {number} _limit - Limit
 * @param {string} _orderBy - Order by field
 * @param {string} order - Order
 * @returns {Promise<Array>}
 */
export const getAllDocsWithPagination = async (
  collectionName,
  startAfterValue = null,
  filters = [],
  _limit = 100,
  _orderBy = 'createdAt',
  order = 'desc',
) => {
  let constraints = [];

  if (filters.length > 0) {
    filters.forEach(filter => {
      constraints.push(where(filter.field, filter.operator, filter.value));
    });
  }

  constraints.push(orderBy(_orderBy, order));

  if (startAfterValue) {
    constraints.push(startAfter(startAfterValue));
  }

  constraints.push(limit(_limit));

  const collectionRef = collection(DB, collectionName);
  const queryRef = query(collectionRef, ...constraints);

  const docs = await getDocs(queryRef);
  const snapshot = await getCountFromServer(collectionRef);
  return {
    list: docs.docs.map(doc => ({ id: doc.id, ...doc.data() })),
    count: snapshot.data().count,
  };
};

/**
 * Get page documents
 * @param {string} collectionName - Collection name
 * @param {string} startAfterValue - Start after value
 * @param {number} _limit - Limit
 * @param {string} _orderBy - Order by field
 * @param {string} order - Order
 * @returns {Promise<Array>}
 */
export const getPageDocs = async (
  collectionName,
  startAfterValue,
  _limit = 100,
  _orderBy = 'createdAt',
  order = 'desc',
) => {
  const collectionRef = collection(DB, collectionName);
  let queryRef = query(collectionRef, orderBy(_orderBy, order));

  if (startAfterValue) {
    queryRef = query(queryRef, startAfter(startAfterValue.createdAt));
  }

  const docs = await getDocs(query(queryRef, limit(_limit)));

  return docs.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

/**
 * Check if a document exists in a collection with a specific field value
 * @param {string} collectionName - Collection name
 * @param {string} field - Field name
 * @param {any} value - Field value
 * @returns {Promise<boolean>}
 */
export const checkDocExist = async (collectionName, field, value) => {
  try {
    const collectionRef = collection(DB, collectionName);
    const q = query(collectionRef, where(field, '==', value));
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    console.error('Error checking document existence:', error);
    return false;
  }
};

/**
 * Search users
 * @param {string} queryValue - Query value
 * @returns {Promise<Array>}
 */
export const searchUsers = async queryValue => {
  const collectionRef = collection(DB, 'users');
  let q = query(collectionRef, where('email', '==', queryValue));
  let docsRef = await getDocs(q);

  if (docsRef.size === 0) {
    q = query(collectionRef, where('id', '==', queryValue));
    docsRef = await getDocs(q);
  }

  return docsRef.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

const LOG_STORAGE_PATHS = ['/logs', '/logs/auth', '/logs/non-auth'];

const serializeFiles = result => {
  return result.reduce(
    (acc, docs) =>
      acc.concat(
        docs.items.map(doc => {
          return {
            name: doc.name,
            path: doc._location.path,
          };
        }),
      ),
    [],
  );
};

/**
 * Search logs
 * @param {Array} queryList - Query list
 * @returns {Promise<Array>}
 */
export const searchLogs = async queryList => {
  const searchablePaths = _.flatten(queryList.map(query => LOG_STORAGE_PATHS.map(path => `${path}/${query}`)));

  const result = await Promise.all(
    searchablePaths.map(async path => {
      const listRef = ref(STORAGE, path);
      const res = await listAll(listRef);
      return res;
    }),
  );

  const data = serializeFiles(result);

  return data;
};

/**
 * Get ticket by id
 * @param {string} collectionName - Collection name
 * @param {string} id - Ticket id
 * @returns {Promise<Object>}
 */
export const getTicketById = async (collectionName, id) => {
  const collectionRef = collection(DB, collectionName);
  const docRef = doc(collectionRef, id);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    const messagesRef = await getDocs(
      query(collection(DB, collectionName, id, 'messages'), orderBy('createdAt', 'asc')),
    );
    return { id: docSnap.id, ...docSnap.data(), messages: messagesRef.docs.map(message => message.data()) };
  }
};

export const getFilteredCount = async (collectionName, filters = [], _orderBy = 'createdAt', order = 'desc') => {
  let constraints = [];

  if (filters.length > 0) {
    filters.forEach(filter => {
      constraints.push(where(filter.field, filter.operator, filter.value));
    });
  }

  constraints.push(orderBy(_orderBy, order));

  const collectionRef = collection(DB, collectionName);
  const queryRef = query(collectionRef, ...constraints);

  const snapshot = await getCountFromServer(queryRef);
  return snapshot.data().count;
};

export default DB;
